import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { cn } from '../cn.js';
import { usePhone } from './hooks.js';
import { Select } from './primitives.js';

// ------------------------------------------------------------------ Tabs (segmented control)

export interface TabItem<V extends string> {
  value: V;
  label: string;
  icon?: LucideIcon;
}

/**
 * Segmented tabs with a sliding glass thumb. Arrow keys move between tabs (mirrored in RTL),
 * Home/End jump. Equal-width segments keep the thumb a pure transform.
 * With `panelId`, each tab controls the <TabPanel> of the same id. Phones get a picker instead
 * once there are more tabs than fit side by side.
 */
export function Tabs<V extends string>({
  items,
  value,
  onChange,
  label,
  panelId,
  className,
}: {
  items: TabItem<V>[];
  value: V;
  onChange: (v: V) => void;
  label: string;
  panelId?: string;
  className?: string;
}) {
  const index = Math.max(
    0,
    items.findIndex((i) => i.value === value),
  );
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const phone = usePhone();

  const onKey = (e: KeyboardEvent) => {
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const step =
      e.key === 'ArrowRight' ? (rtl ? -1 : 1) : e.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
    let next = index;
    if (step) next = (index + step + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else return;
    e.preventDefault();
    onChange(items[next]!.value);
    refs.current[next]?.focus();
  };

  if (phone && items.length > 4)
    return (
      <Select
        aria-label={label}
        id={panelId ? `${panelId}-tab-${items[index]!.value}` : undefined}
        aria-controls={panelId}
        value={items[index]!.value}
        onChange={(e) => onChange(e.target.value as V)}
        className={className}
      >
        {items.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
    );

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKey}
      className={cn(
        'relative grid auto-cols-fr grid-flow-col rounded-control bg-[var(--control-bg)] p-1 shadow-[inset_0_0_0_1px_var(--ui-line)]',
        className,
      )}
    >
      <span
        aria-hidden
        className="material pointer-events-none absolute inset-y-1 start-1 rounded-[10px] shadow-sm transition-transform duration-[var(--dur-3)] ease-spring"
        style={{
          width: `calc((100% - 0.5rem) / ${items.length})`,
          transform: `translateX(calc(${index} * 100% * var(--tab-dir, 1)))`,
        }}
      />
      {items.map((item, i) => {
        const Icon = item.icon;
        const selected = i === index;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={panelId ? `${panelId}-tab-${item.value}` : undefined}
            aria-controls={panelId && selected ? panelId : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            className={cn(
              'pressable focus-ring tap-target relative z-[1] flex h-9 min-w-0 items-center justify-center gap-2 rounded-[10px] px-3 text-sm font-medium',
              selected ? 'text-fg' : 'text-muted hover:text-fg',
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The content of the selected tab of the <Tabs> given the same `id` as `panelId`. */
export function TabPanel<V extends string>({
  id,
  value,
  children,
}: {
  id: string;
  value: V;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={`${id}-tab-${value}`}
      key={value}
      className="mt-6 animate-fade"
    >
      {children}
    </div>
  );
}

/** The selected tab, kept in the address (?tab=) so reloads and shared links open the same tab. */
export function useTabParam<V extends string>(values: readonly V[]): [V, (v: V) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const current = values.find((v) => v === raw) ?? values[0]!;
  const select = (v: V) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === values[0]) next.delete('tab');
        else next.set('tab', v);
        return next;
      },
      { replace: true },
    );
  return [current, select];
}

// ------------------------------------------------------------------ Menu (dropdown)

export interface MenuItemProps {
  icon?: LucideIcon;
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  /** Marks the current choice (radio-like menus). */
  checked?: boolean;
  /** Injected by <Menu>. */
  close?: () => void;
}

export function MenuItem({
  icon: Icon,
  children,
  onSelect,
  danger,
  checked,
  close,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      onClick={() => {
        close?.();
        onSelect();
      }}
      className={cn(
        'flex min-h-11 w-full items-center gap-3 rounded-[10px] px-3 text-start text-sm font-medium outline-none',
        'transition-colors duration-[var(--dur-1)] hover:bg-[var(--control-bg-hover)] focus-visible:bg-[var(--control-bg-hover)]',
        danger ? 'text-danger' : 'text-fg',
      )}
    >
      {Icon && <Icon className={cn('size-4 shrink-0', !danger && 'text-muted')} aria-hidden />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {checked && <span className="size-2 rounded-full bg-brand" aria-hidden />}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="mx-2 my-1 h-px bg-[var(--ui-line)]" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted" role="presentation">
      {children}
    </div>
  );
}

/**
 * Dropdown menu in the top layer (popover=auto: light dismiss + Escape for free), positioned
 * under its trigger and kept inside the viewport. Arrow keys move focus; focus returns to the
 * trigger on close.
 */
export function Menu({
  trigger,
  children,
  align = 'end',
  label,
  className,
}: {
  trigger: (props: {
    ref: (el: HTMLButtonElement | null) => void;
    onClick: () => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
  }) => ReactNode;
  children: ReactNode;
  align?: 'start' | 'end';
  label: string;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position + show in the top layer.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const btn = triggerRef.current;
    if (!menu || !btn || !open) return;
    const place = () => {
      const r = btn.getBoundingClientRect();
      const rtl = getComputedStyle(btn).direction === 'rtl';
      const width = menu.offsetWidth;
      const gap = 8;
      const alignEnd = align === 'end' ? !rtl : rtl;
      let left = alignEnd ? r.right - width : r.left;
      left = Math.min(Math.max(gap, left), window.innerWidth - width - gap);
      let top = r.bottom + gap;
      if (top + menu.offsetHeight > window.innerHeight - gap)
        top = Math.max(gap, r.top - gap - menu.offsetHeight);
      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
    };
    try {
      menu.showPopover?.();
    } catch {
      // already open
    }
    place();
    const first = menu.querySelector<HTMLElement>('[role^="menuitem"]');
    first?.focus();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  // Popover light-dismiss closes natively; mirror it into state and restore focus.
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const onToggle = (e: Event) => {
      if ((e as ToggleEvent).newState === 'closed') {
        setOpen(false);
      }
    };
    menu.addEventListener('toggle', onToggle);
    return () => menu.removeEventListener('toggle', onToggle);
  }, [open]);

  useEffect(() => {
    if (open) return;
    const menu = menuRef.current;
    try {
      if (menu?.matches(':popover-open')) menu.hidePopover();
    } catch {
      // not a popover
    }
  }, [open]);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]'));
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') items[(i + 1) % items.length]?.focus();
    else if (e.key === 'ArrowUp') items[(i - 1 + items.length) % items.length]?.focus();
    else if (e.key === 'Home') items[0]?.focus();
    else if (e.key === 'End') items[items.length - 1]?.focus();
    else if (e.key === 'Escape') {
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'Tab') setOpen(false);
    else return;
    e.preventDefault();
  };

  const withClose = Children.map(children, (child) =>
    isValidElement(child) && child.type === MenuItem
      ? cloneElement(child as ReactElement<MenuItemProps>, {
          close: () => {
            setOpen(false);
            triggerRef.current?.focus();
          },
        })
      : child,
  );

  return (
    <>
      {trigger({
        ref: (el) => {
          triggerRef.current = el;
        },
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
      })}
      {open && (
        <div
          ref={menuRef}
          id={id}
          role="menu"
          aria-label={label}
          popover="auto"
          onKeyDown={onKey}
          className={cn(
            'ui-menu glass-thick fixed z-[var(--z-overlay)] w-64 max-w-[calc(100vw-1rem)] rounded-[20px] text-fg',
            className,
          )}
        >
          {withClose}
        </div>
      )}
    </>
  );
}
